/**
 * SectionRepliable — encadré pliable/dépliable avec une flèche.
 *
 * Permet à l'administré de replier les blocs (menus, factures, pièces…) pour
 * avoir une vue d'ensemble compacte, et de ne déplier que ce qui l'intéresse.
 * Déplié par défaut (sauf defaultOpen=false).
 */

import { useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { Card } from '@/components/ui';

export function SectionRepliable({
  title,
  icon,
  defaultOpen = true,
  right,
  children,
  className = 'mb-4',
  bare = false,
  header,
}: {
  /** Titre texte. Optionnel si `header` (en-tête personnalisé) est fourni. */
  title?: string;
  icon?: ReactNode;
  defaultOpen?: boolean;
  /** Contenu optionnel aligné à droite de l'en-tête (badge, action…). */
  right?: ReactNode;
  children: ReactNode;
  className?: string;
  /** Si true, ne pas envelopper dans une Card (le parent gère son propre cadre). */
  bare?: boolean;
  /** En-tête personnalisé (remplace title/icon) — affiché dans le bouton cliquable. */
  header?: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);

  const inner = (
    <>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="flex flex-1 items-center gap-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-bg rounded-r-md"
        >
          <ChevronDown
            className={`h-4 w-4 flex-shrink-0 text-ink-muted transition-transform duration-200 ${open ? '' : '-rotate-90'}`}
            aria-hidden="true"
          />
          {header ?? (
            <>
              {icon}
              <h2 className="font-display text-base font-semibold text-ink">{title}</h2>
            </>
          )}
        </button>
        {right}
      </div>
      {open && <div className="mt-3">{children}</div>}
    </>
  );

  if (bare) {
    return <div className={className}>{inner}</div>;
  }

  return (
    <Card className={className}>
      <div className="p-4">{inner}</div>
    </Card>
  );
}
